/** Id of the unencrypted fMP4 initialization segment. */
export const INIT_SEGMENT_ID = 'init.mp4';

/** Valid swarm segment ids: `segNNNNNN.m4s` media segments and `init.mp4`. */
export const SEGMENT_ID_REGEX = /^(seg\d+\.m4s|init\.mp4)$/;

export function isValidSegmentId(segmentId: string): boolean {
  return SEGMENT_ID_REGEX.test(segmentId);
}

/**
 * Canonical segment ID used across segmenter, tracker, P2P swarm, and player.
 * Always the segment basename (e.g. "seg000042.m4s" or "init.mp4"), never full paths.
 */
export function canonicalSegmentId(urlOrPath: string): string {
  const withoutQuery = urlOrPath.split('?')[0] ?? urlOrPath;
  return withoutQuery.split('/').pop() ?? withoutQuery;
}

/** Media segment id for a sequence number: 42 → `seg000042.m4s`. */
export function segmentIdForSeq(seq: number): string {
  return `seg${String(seq).padStart(6, '0')}.m4s`;
}

/** Sequence number of a media segment id, or null (e.g. for `init.mp4`). */
export function segmentSequence(segmentId: string): number | null {
  const match = canonicalSegmentId(segmentId).match(/^seg0*(\d+)\.m4s$/);
  return match?.[1] ? Number.parseInt(match[1], 10) : null;
}

/** Cache/store key scoped by rendition (v1 always `main`). */
export function segmentCacheKey(renditionId: string, segmentId: string): string {
  return `${renditionId}:${canonicalSegmentId(segmentId)}`;
}

/** True for seed peer ids (`seed-` prefix). Prefer the tracker-provided role when available. */
export function isSeedPeerId(peerId: string): boolean {
  return peerId.startsWith('seed-');
}

/**
 * Hash election: pick one peer in the swarm to fetch a segment from the origin.
 * All other peers should wait for P2P to avoid duplicate origin hits.
 */
export function electOriginFetcherPeerId(
  segmentId: string,
  candidatePeerIds: string[],
): string {
  const pool = [...new Set(candidatePeerIds)].sort();
  if (pool.length === 0) return '';
  const id = canonicalSegmentId(segmentId);
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return pool[Math.abs(hash) % pool.length]!;
}

/**
 * Sticky election: the same peer fetches `window` consecutive segments from the
 * origin, so the seed uplink sees one requester per window instead of a new one
 * per segment. Returns '' for an empty pool.
 */
export function electStickyFetcherPeerId(
  seq: number,
  pool: string[],
  window = 8,
): string {
  if (pool.length === 0) return '';
  return pool.slice().sort()[Math.floor(seq / window) % pool.length]!;
}

/**
 * Senior election: the viewer that joined the swarm first fetches every segment from the origin
 * and the others relay from it, so a newcomer never moves origin traffic (and its relay wait)
 * onto viewers already playing. Peers without a known join time rank after those with one; ties
 * break on peer id so every peer computes the same answer. Returns '' for an empty pool.
 */
export function electSeniorFetcherPeerId(
  pool: string[],
  joinedAt: (peerId: string) => number | undefined,
): string {
  if (pool.length === 0) return '';
  const rank = (id: string) => joinedAt(id) ?? Number.POSITIVE_INFINITY;
  return [...new Set(pool)].sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0))[0]!;
}
